import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaMinus } from 'react-icons/fa';
import './ExecutiveTrainingDetail.css';

const flattenExecutiveTrainingData = (data) => {
  return data.reduce((acc, item) => {
    if (item.subItems) {
      return [
        ...acc,
        { path: item.path, title: item.title, description: item.description },
        ...item.subItems.map((subItem) => ({
          path: subItem.path,
          title: subItem.title,
          description: subItem.description,
        })),
      ];
    }
    return [...acc, { path: item.path, title: item.title, description: item.description }];
  }, []);
};

const ExecutiveTrainingDetail = ({ training, executiveTrainingData }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const flattenedData = useMemo(
    () => flattenExecutiveTrainingData(executiveTrainingData),
    [executiveTrainingData]
  );

  const currentPath = location.pathname;
  const activeItem =
    training || flattenedData.find((i) => i.path === currentPath) || null;

  const [expandedItems, setExpandedItems] = useState([]);

  // Auto-expand the active item when landing on a route (no scrolling)
  useEffect(() => {
    if (activeItem?.path) setExpandedItems([activeItem.path]);
    else setExpandedItems([]);
  }, [activeItem?.path]);

  const toggleItem = (path) => {
    setExpandedItems((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [path]
    );
  };

  const handleSelect = (item) => {
    if (currentPath !== item.path) navigate(item.path); // update URL
    setExpandedItems([item.path]); // show content in dropdown
  };

  const isAllTrainings = !activeItem;

  return (
    <>
      <div className="executive-training-detail-container">
        {/* Desktop two-column stays the same */}
        {!isAllTrainings && (
          <div className="executive-training-grid">
            <aside className="executive-training-sidebar" aria-label="Executive Training Navigation">
              <ul>
                {flattenedData.map((item, index) => (
                  <li key={index}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        isActive ? 'executive-training-link active' : 'executive-training-link'
                      }
                    >
                      {item.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </aside>

            <main className="executive-training-main-content">
              <h1>{activeItem.title}</h1>
              <p>{activeItem.description}</p>
            </main>
          </div>
        )}

        {/* Title for the “all trainings” view */}
        {isAllTrainings && (
          <h2 className="executive-training-title">Our Executive Training Courses</h2>
        )}

        {/* Accordion (mobile-friendly): shows content once; clicking updates URL */}
        <div className="executive-training-accordion" role="list">
          {flattenedData.map((item, index) => {
            const isExpanded = expandedItems.includes(item.path);
            const isActive = activeItem && activeItem.path === item.path;

            return (
              <div className={`accordion-item ${isActive ? 'is-active' : ''}`} key={index} role="listitem">
                <div
                  id={`accordion-header-${index}`}
                  className={`accordion-header ${isExpanded ? 'active' : ''}`}
                  onClick={() => handleSelect(item)}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-controls={`accordion-content-${index}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelect(item);
                  }}
                >
                  <span>{item.title}</span>
                  <span className="accordion-icon">
                    {isExpanded ? <FaMinus /> : <FaPlus />}
                  </span>
                </div>
                <div
                  id={`accordion-content-${index}`}
                  className="accordion-content"
                  role="region"
                  aria-labelledby={`accordion-header-${index}`}
                >
                  {isExpanded && <p>{item.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ExecutiveTrainingDetail;
